/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import nanoid from 'nanoid';
import PropTypes from 'prop-types';

import { Popover, TextField } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';

// components
import OutsideClickListener from '@nile/components/src/Components/CreatableDropdown/components/OutsideClickListener';
import EditAndDeleteOption from '@nile/components/src/Components/CreatableDropdown/components/EditAndDeleteOption';
import { TEXT_LENGTH } from '@nile/components/src/Constants';

// helpers
import {
  isAvailable,
  getFilteredOptions
} from '@nile/components/src/Components/CreatableDropdown/helpers';
import { noop } from 'lodash';
import { Loader } from '@nile/components';

const CreatableSelect = ({
  options,
  selectedOptions,
  loading,
  updateOptionsList = (modifiedOptions) => {},
  updateSelectedOptions = (modifiedOptions) => {},
  onOptionEdit,
  onOptionDelete,
  onOptionCreate,
  onOptionEditStateChange = noop,
  isEdit = true,
  isDelete = true,
  isCreate = true,
  isMulti = false,
  disabled = false,
  createLabel = 'Create Option',
  onMenuOpen = noop
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOptionState, setCreateOptionState] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [currentFocus, setCurrentFocus] = useState(0);

  const wrapperRef = useRef();

  const createOptionRef = useRef();

  const textFieldDisabled =
    disabled || options.reduce((acc, { editState }) => acc || editState, false);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const selectOptions = (option, closeMenuAfterSelect = true) => {
    if (isMulti) {
      updateSelectedOptions([...selectedOptions, option]);
      // multiselect logic to be coded
    } else {
      updateSelectedOptions([option]);
      setTextValue(option?.label || '');
    }
    if (closeMenuAfterSelect) {
      closeMenu();
      // remove focus from input
      document.activeElement.blur();
    }
  };

  const handleTextChange = (value) => {
    setTextValue(value);
  };

  const handleTextBlur = () => {
    setTextValue(textValue.trim());
  };

  const handleNewOptionTextChange = (value) => {
    setNewOptionValue(value);
  };

  const handleNewOptionBlur = () => {
    setNewOptionValue('');
    setCreateOptionState(false);
  };

  const createOption = async (value) => {
    const newOption = { label: value, value, id: `${nanoid()}`, isNew: true };
    updateOptionsList([...options, newOption]);
    if (onOptionCreate) onOptionCreate(newOption);
    selectOptions(newOption);
  };

  const handleAcceptNewOption = (event) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      const value = newOptionValue.trim();
      if (value) {
        handleNewOptionBlur();
        const availableOption = isAvailable(options, value);
        if (availableOption) {
          selectOptions(availableOption);
          return;
        }
        createOption(value);
      }
    }
  };

  const updateLocalOptions = (option, value, mode) => {
    const localOptionsCopy = [...options];

    if (mode === 'editState') {
      const editedOptionIndex = options.findIndex((el) => {
        if (el.id) return el.id === option.id;
        return el.label === option.label;
      });
      localOptionsCopy[editedOptionIndex].editState = value;
      onOptionEditStateChange(option);
      updateOptionsList(localOptionsCopy);
    }

    if (mode === 'edit') {
      if (isAvailable(options, value)) return;
      const editedOptionIndex = options.findIndex((el) => {
        if (el.id) return el.id === option.id;
        return el.label === option.label;
      });
      localOptionsCopy[editedOptionIndex].value = value;
      localOptionsCopy[editedOptionIndex].label = value;
      if (onOptionEdit) onOptionEdit(localOptionsCopy[editedOptionIndex]);
      updateOptionsList(localOptionsCopy);

      const editedOptionSelectedIdx = selectedOptions.findIndex(
        ({ id }) => id === option.id
      );
      if (editedOptionSelectedIdx > -1) {
        const selectedOptionsCopy = [...selectedOptions];
        selectedOptionsCopy[editedOptionSelectedIdx].value = value;
        selectedOptionsCopy[editedOptionSelectedIdx].label = value;
        updateSelectedOptions(selectedOptionsCopy);
      }
    }

    if (mode === 'delete') {
      const updatedLocalOptions = options.filter((el) => {
        if (el.id) return el.id !== option.id;
        return el.label !== option.label;
      });
      if (onOptionDelete)
        onOptionDelete(options.find((el) => el.id === option.id));
      updateOptionsList(updatedLocalOptions);

      const deletedOptionSelectedIdx = selectedOptions.findIndex(
        ({ id }) => id === option.id
      );
      if (deletedOptionSelectedIdx > -1) {
        const updatedSelectedOptions = selectedOptions.filter((el) => {
          if (el.id) return el.id !== option.id;
          return el.label !== option.label;
        });
        updateSelectedOptions(updatedSelectedOptions);
      }
    }
  };

  const getTextValuePlaceholder = (clear) => {
    if (isMulti) {
      // multiselect logic to be coded
      return '';
    }
    return selectedOptions?.[0]?.label || 'Enter category';
  };

  useEffect(() => {
    if (currentFocus > getFilteredOptions(options, textValue)?.length - 1) {
      setCurrentFocus(0);
    }
  }, [currentFocus, options, textValue]);

  const focusElementAfterEdit = () => {
    if (wrapperRef.current) wrapperRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (createOptionState) return; // ignore keydown events if create state is enabled
    const size = getFilteredOptions(options, textValue)?.length;
    if (e.keyCode === 40) {
      // Down arrow
      e.preventDefault();
      setCurrentFocus(currentFocus >= size - 1 ? 0 : currentFocus + 1);
    } else if (e.keyCode === 38) {
      // Up arrow
      e.preventDefault();
      setCurrentFocus(currentFocus === 0 ? size - 1 : currentFocus - 1);
    }

    if (e.key === 'Enter') {
      // Enter
      const focusedOption = getFilteredOptions(options, textValue)[
        currentFocus
      ];
      if (focusedOption) {
        selectOptions(focusedOption);
      }
      // Handling for automated create option
      if (!size && isCreate) {
        createOption(textValue.trim());
        setTextValue('');
      }
    }
  };

  useLayoutEffect(() => {
    // To scroll and show all the available options in a list
    if (menuOpen) {
      setTimeout(() => {
        if (createOptionRef.current) {
          createOptionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [menuOpen]);

  useEffect(() => {
    setTextValue(selectedOptions[0]?.label || '');
  }, [selectedOptions]);

  return (
    <div
      className="position-relative fw flex fcol"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <Popover
        active={menuOpen}
        fullWidth
        ref={wrapperRef}
        preferredAlignment="left"
        onClose={() => setMenuOpen(false)}
        activator={
          <div
            className="textfield flex center fw cursor-pointer"
            onClick={() => {
              if (textFieldDisabled) return;
              if (!menuOpen) {
                setMenuOpen(true);
                setTextValue('');
                if (onMenuOpen) onMenuOpen();
              }
            }}
          >
            <div className="f11">
              <TextField
                value={textValue}
                onChange={handleTextChange}
                onBlur={handleTextBlur}
                disabled={textFieldDisabled}
                placeholder={getTextValuePlaceholder()}
                onClearButtonClick={() => {
                  updateSelectedOptions([]);
                }}
                autoComplete="off"
                noborder
                clearButton
                autoFocus={menuOpen}
                maxLength={TEXT_LENGTH.size_5}
                suffix={<SVGIcon name="ArrowDown" />}
              />
            </div>
          </div>
        }
      >
        <>
          <div className="flex fcol fhp">
            {loading ? (
              <div className="flex-center min-height-50">
                <Loader />
              </div>
            ) : (
              <div className="flex fcol f11 scroll max-height-245">
                {getFilteredOptions(options, textValue).map((option, index) => (
                  <EditAndDeleteOption
                    key={option.id || option.label}
                    option={option}
                    updateOptions={updateLocalOptions}
                    selectOption={selectOptions}
                    isSelected={selectedOptions
                      .map(({ label }) => label)
                      .includes(option.label)}
                    isEdit={isEdit}
                    isDelete={isDelete}
                    focus={currentFocus === index}
                    setFocus={setCurrentFocus}
                    index={index}
                    focusElementAfterEdit={focusElementAfterEdit}
                  />
                ))}
              </div>
            )}
            {isCreate &&
              (loading ? (
                <></>
              ) : (
                <div className="flex p5 center mbt5">
                  {(!!getFilteredOptions(options, textValue).length ||
                    !options?.length) && (
                    <span
                      className="ml8 mr8 link-block"
                      ref={createOptionRef}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCreateOptionState(true);
                        if (!newOptionValue)
                          setNewOptionValue(textValue.trim());
                      }}
                    >
                      {createLabel}
                    </span>
                  )}
                  {createOptionState && (
                    <div onKeyDown={handleAcceptNewOption}>
                      <TextField
                        value={newOptionValue}
                        onChange={handleNewOptionTextChange}
                        onBlur={handleNewOptionBlur}
                        placeholder="Enter value"
                        autoComplete="off"
                        focused
                        maxLength={TEXT_LENGTH.size_4}
                      />
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      </Popover>
    </div>
  );
};

const OptionShape = {
  value: PropTypes.string,
  label: PropTypes.string,
  editState: PropTypes.bool,
  id: PropTypes.string
};

CreatableSelect.defaultProps = {
  onOptionEdit: () => {},
  onOptionDelete: () => {},
  onOptionCreate: () => {},
  isEdit: true,
  isDelete: true,
  isCreate: true,
  isMulti: false,
  disabled: false
};

CreatableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired,
  updateOptionsList: PropTypes.func.isRequired,
  updateSelectedOptions: PropTypes.func.isRequired,
  onOptionEdit: PropTypes.func,
  onOptionDelete: PropTypes.func,
  onOptionCreate: PropTypes.func,
  isEdit: PropTypes.bool,
  isDelete: PropTypes.bool,
  isCreate: PropTypes.bool,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool
};

export default CreatableSelect;
